import type { BrandConfig } from "../../config/types.ts";
import { resolveBrandVisual } from "./placeholder.ts";
import { getSavedAuthToken, clearAuthToken } from "./auth-interceptor.ts";
import Swal from "sweetalert2";

/** Sincroniza el estado colapsado del lateral en <html> para poder centrar el contenido por CSS */
export function syncSidebarCollapse(): void {
  const btn = document.querySelector<HTMLButtonElement>("[data-sidebar-toggle]");
  const collapsed = btn ? btn.getAttribute("aria-expanded") !== "true" : false;
  if (collapsed) {
    document.documentElement.dataset.sidebarCollapsed = "true";
    document.body.classList.add("sidebar-collapsed");
  } else {
    delete document.documentElement.dataset.sidebarCollapsed;
    document.body.classList.remove("sidebar-collapsed");
  }
}

// Reconstruye el pie de Zudoku como una única línea centrada de la marca activa
export function renderBrandFooter(brand: BrandConfig): void {
  const footer = Array.from(document.querySelectorAll<HTMLElement>("footer"))
    .filter((el) => el.querySelector("a") || !el.querySelector("main"))
    .pop();
  const target = document.querySelector<HTMLElement>("footer.bg-background") || footer;
  if (!target) return;

  const year = new Date().getFullYear();
  const owner =
    brand.footer.companyName ||
    brand.contentOverrides?.companyName ||
    brand.metadata.companyName ||
    brand.name;
  const text = brand.footer.copyright || `Copyright © ${year} ${owner}`;
  const website = brand.footer.website || brand.metadata.canonicalUrl || brand.metadata.docsUrl || "/";

  const container = document.createElement("div");
  container.className = "mx-auto max-w-screen-2xl px-4 lg:px-8 py-6 flex justify-center";
  container.style.minHeight = "48px";

  const link = document.createElement("a");
  link.href = website;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.className = "brand-footer-copyright inline-flex items-center gap-1 font-semibold text-sm";
  link.style.color = "var(--primary)";
  link.textContent = text;
  link.insertAdjacentHTML(
    "beforeend",
    `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1 2-2V8a2 2 0 0 1 2-2h6"/></svg>`
  );

  container.appendChild(link);
  target.replaceChildren(container);
}

// Oculta el selector de servidor bajo los encabezados de tag
export function syncTagServerDisplay(): void {
  const serverSelects = document.querySelectorAll<HTMLSelectElement>("select[aria-label='Select server']");
  serverSelects.forEach((select) => {
    const container = select.closest("div");
    if (container && !container.classList.contains("server-select-hidden")) {
      container.classList.add("server-select-hidden");
    }
  });
}

/** Sincroniza título y favicon del <head> con la marca activa */
export function syncHeadMetadata(brand: BrandConfig): void {
  const faviconTarget = resolveBrandVisual(brand, "favicon");
  const titleTarget = brand.metadata.title || document.title;

  if (brand.metadata.title) {
    const titleEl = document.querySelector("title");
    if (titleEl && titleEl.textContent !== titleTarget) titleEl.textContent = titleTarget;
    if (document.title !== titleTarget) document.title = titleTarget;
  }

  document.querySelectorAll<HTMLLinkElement>('link[rel="icon"],link[rel="shortcut icon"]').forEach((l) => {
    if (l.getAttribute("href") !== faviconTarget) l.remove();
  });
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="icon"][href]');
  if (!existing) {
    const created = document.createElement("link");
    created.rel = "icon";
    created.href = faviconTarget;
    document.head.appendChild(created);
  }
}

/** Localiza el enlace del portal por href antes que por texto (estable ante i18n */
function findPortalLink(): HTMLAnchorElement | undefined {
  const anchors = Array.from(document.querySelectorAll<HTMLAnchorElement>("header a"));
  return (
    anchors.find((a) => a.href.includes("portal")) ??
    anchors.find((a) => a.textContent?.trim() === "Go to the Portal" || a.textContent?.trim() === "Log In")
  );
}

function findSignupLink(): HTMLAnchorElement | undefined {
  const anchors = Array.from(document.querySelectorAll<HTMLAnchorElement>("header a"));
  return (
    anchors.find((a) => a.href.includes("register")) ??
    anchors.find((a) => a.textContent?.trim() === "Sign Up")
  );
}

/** Sincroniza logo y enlaces externos de la cabecera (portal y registro) */
export function syncHeaderElements(brand: BrandConfig): void {
  const targetLogo = resolveBrandVisual(brand, "logo");
  document.querySelectorAll<HTMLImageElement>("header a img").forEach((img) => {
    if (img.getAttribute("src") !== targetLogo) {
      img.setAttribute("src", targetLogo);
      img.alt = brand.displayName;
      img.onerror = () => {
        if (!targetLogo.startsWith("data:")) img.setAttribute("src", targetLogo);
      };
    }
  });

  const loginLink = findPortalLink();
  const signupLink = findSignupLink();
  if (loginLink) {
    loginLink.textContent = "Go to the Portal";
    if (brand.contentOverrides?.portalUrl) {
      loginLink.href = brand.contentOverrides.portalUrl;
    }
  }
  if (signupLink && brand.contentOverrides?.registerUrl) {
    signupLink.href = brand.contentOverrides.registerUrl;
  }

  syncAuthStatusIndicator();
}

/**
 * Muestra la insignia AUTH junto al botón del portal cuando hay token guardado
 * La insignia abre el modal de sesión; si no hay token, se retira del DOM
 */
export function syncAuthStatusIndicator(): void {
  const headerAnchors = Array.from(document.querySelectorAll<HTMLAnchorElement>("header a"));
  const portalLink =
    (findPortalLink() ??
    headerAnchors.find((a) => a.textContent?.trim() === "Sign Up")) ||
    document.querySelector<HTMLElement>("header [data-slot='navigation-menu']");

  if (!portalLink) return;

  const parent = portalLink.parentElement;
  if (!parent) return;

  const currentToken = getSavedAuthToken();
  const existingBadge = document.getElementById("api-auth-badge");

  if (!currentToken) {
    if (existingBadge) existingBadge.remove();
    return;
  }

  if (existingBadge) {
    return;
  }

  const badge = document.createElement("div");
  badge.id = "api-auth-badge";
  badge.className = "api-auth-badge-container";
  badge.title = "Cuenta API Conectada (Click para ver detalles o cerrar sesión)";
  badge.setAttribute("role", "button");
  badge.setAttribute("tabindex", "0");
  badge.setAttribute("aria-label", "API Authenticated");

  badge.innerHTML = `
    <span class="api-auth-pulse-dot" aria-hidden="true">
      <span class="api-auth-pulse-ring"></span>
      <span class="api-auth-solid-dot"></span>
    </span>
    <span class="api-auth-text">AUTH</span>
  `;

  const openAuthModal = () => {
    Swal.fire({
      title: `<span style="font-size: 18px; font-weight: 700; color: #0f172a; letter-spacing: -0.01em;">API Session Active</span>`,
      html: `
        <div style="text-align: left; font-size: 13px; color: #475569; display: flex; flex-direction: column; gap: 10px; margin-top: 6px;">
          <div style="display: flex; align-items: center; gap: 8px; background: rgba(16, 185, 129, 0.08); border: 1px solid rgba(16, 185, 129, 0.25); padding: 8px 12px; border-radius: 8px;">
            <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #10b981; box-shadow: 0 0 6px #10b981;"></span>
            <span style="font-weight: 600; color: #065f46; font-size: 12px;">Authenticated with Bearer Token</span>
          </div>
          <p style="margin: 0; line-height: 1.5;">
            Your requests in the <strong>API Reference</strong> and <strong>Playground</strong> are automatically signed and authorized.
          </p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Log Out",
      cancelButtonText: "Close",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
      focusCancel: true,
      width: "380px",
      padding: "20px",
      customClass: {
        popup: "api-auth-swal-popup",
        confirmButton: "api-auth-swal-danger-btn",
        cancelButton: "api-auth-swal-close-btn",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        clearAuthToken();
        badge.remove();

        // Toast compacto en lugar de diálogo grande.
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Logged out from API",
          showConfirmButton: false,
          timer: 2000,
          timerProgressBar: true,
          customClass: {
            popup: "api-auth-toast-popup",
          },
        });
      }
    });
  };

  badge.addEventListener("click", openAuthModal);
  badge.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openAuthModal();
    }
  });

  parent.insertBefore(badge, portalLink);
}

/** Aplica las variables CSS de la marca (primary, secondary, accent, ring) */
export function applyBrandThemeColors(brand: BrandConfig, isDark = false): void {
  const root = document.documentElement;
  if (!brand.colors.primary) return;

  const lightPrimary = brand.colors.light?.primary || brand.colors.primary;
  const darkPrimary = brand.colors.dark?.primary || brand.colors.primary;
  const lightSecondary = brand.colors.light?.secondary || brand.colors.secondary || "#24234E";
  const darkSecondary = brand.colors.dark?.secondary || brand.colors.secondary || "#24234E";
  const lightRing = brand.colors.light?.ring || lightPrimary;
  const darkRing = brand.colors.dark?.ring || darkPrimary;
  const accent = brand.colors.accent || brand.colors.primary;

  const primary = isDark ? darkPrimary : lightPrimary;
  const ring = isDark ? darkRing : lightRing;
  const secondary = isDark ? darkSecondary : lightSecondary;

  root.style.setProperty("--primary", primary, "important");
  root.style.setProperty("--ring", ring, "important");
  root.style.setProperty("--accent", accent, "important");
  root.style.setProperty("--secondary", secondary, "important");
  root.style.setProperty("--secondary-foreground", "#ffffff", "important");
  root.style.setProperty("--primary-foreground", "#ffffff", "important");

  let style = document.getElementById("brand-theme-override") as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement("style");
    style.id = "brand-theme-override";
    document.head.appendChild(style);
  }
  style.textContent = `
    :root { --primary: ${lightPrimary} !important; --ring: ${lightRing} !important; --accent: ${accent} !important; --secondary: ${lightSecondary} !important; --secondary-foreground: #ffffff !important; }
    .dark { --primary: ${darkPrimary} !important; --ring: ${darkRing} !important; --accent: ${accent} !important; --secondary: ${darkSecondary} !important; --secondary-foreground: #ffffff !important; }
    .server-select-hidden { display: none !important; }
  `;
}